import type { MessageDescriptor } from 'react-intl';

import { aiMessages } from '@atlaskit/editor-common/ai-messages/ai';
import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages/ai-config-items';
import { AICasualIcon } from '@atlaskit/editor-toolbar/ai-casual-icon';
import { AIHeartIcon } from '@atlaskit/editor-toolbar/ai-heart-icon';
import { AIBriefcaseIcon } from '@atlaskit/editor-toolbar/ai-professional-icon';

import type {
	SelectionToolbarDropdownConfig,
	WritingStyleMenuConfiguration,
	WritingStyleMenuItem,
	WritingStyleMenuRewriteItem,
	WritingStyleMenuSurface,
} from '../../types/types';
import type {
	EditorPluginAIConfigItem,
	EditorPluginAIConfigItemFactoryOptions,
	EditorPluginAIConfigItemWithOptions,
} from './config-items/config-items';
import {
	changeToneToCasualWithOptions,
	changeToneToEducationalWithOptions,
	changeToneToEmpatheticWithOptions,
	changeToneToNeutralWithOptions,
	changeToneToProfessionalWithOptions,
} from './config-items/prompts/change-tone/change-tone';

const placements = (
	values: Partial<Record<WritingStyleMenuSurface, number>>,
): Partial<Record<WritingStyleMenuSurface, number>> => values;

export const createLegacyConfluenceWritingStyleMenu = (
	options: EditorPluginAIConfigItemFactoryOptions,
): WritingStyleMenuConfiguration => ({
	label: aiMessages.changeToneToolbarDropdownIconTitle,
	surfaceLabels: {
		'selection-toolbar': aiMessages.confluenceChangeToneSelectionToolbarDropdownMenuTitle,
	},
	items: [
		{
			type: 'rewrite',
			id: 'more-professional-menu-item',
			label: aiConfigItemMessages.changeToneProfessionalToneSelectionToolbarDropdownItemTitle,
			toolbarLabel: aiMessages.moreProfessionalToneToolbarDropdownIconTitle,
			configItem: changeToneToProfessionalWithOptions(options),
			toolbarIcon: AIBriefcaseIcon,
			placements: placements({
				command: 0,
				'dynamic-smart-button-legacy': 0,
				'dynamic-smart-button-streaming': 2,
				'object-sidebar': 0,
				'selection-toolbar': 0,
				toolbar: 0,
			}),
		},
		{
			type: 'rewrite',
			id: 'more-empathetic-menu-item',
			label: aiConfigItemMessages.changeToneEmpatheticToneSelectionToolbarDropdownItemTitle,
			toolbarLabel: aiMessages.moreEmpatheticToneToolbarDropdownIconTitle,
			configItem: changeToneToEmpatheticWithOptions(options),
			toolbarIcon: AIHeartIcon,
			placements: placements({
				command: 1,
				'dynamic-smart-button-legacy': 1,
				'dynamic-smart-button-streaming': 1,
				'object-sidebar': 2,
				'selection-toolbar': 1,
				toolbar: 2,
			}),
		},
		{
			type: 'rewrite',
			id: 'more-casual-menu-item',
			label: aiConfigItemMessages.changeToneCasualToneSelectionToolbarDropdownItemTitle,
			toolbarLabel: aiMessages.moreCasualToneToolbarDropdownIconTitle,
			configItem: changeToneToCasualWithOptions(options),
			toolbarIcon: AICasualIcon,
			placements: placements({
				command: 2,
				'dynamic-smart-button-legacy': 2,
				'dynamic-smart-button-streaming': 0,
				'object-sidebar': 1,
				'selection-toolbar': 2,
				toolbar: 1,
			}),
		},
		{
			type: 'rewrite',
			id: 'change-tone-neutral-menu-item',
			label: aiConfigItemMessages.changeToneNeutralToneSelectionToolbarDropdownItemTitle,
			configItem: changeToneToNeutralWithOptions(options),
			toolbarIcon: AIBriefcaseIcon,
			placements: placements({ 'selection-toolbar': 3 }),
		},
		{
			type: 'rewrite',
			id: 'change-tone-educational-menu-item',
			label: aiConfigItemMessages.changeToneEducationalToneSelectionToolbarDropdownItemTitle,
			configItem: changeToneToEducationalWithOptions(options),
			toolbarIcon: AIBriefcaseIcon,
			placements: placements({
				command: 3,
				'dynamic-smart-button-legacy': 3,
				'selection-toolbar': 4,
			}),
		},
	],
});

export const getWritingStyleMenuItemsForSurface = (
	configuration: WritingStyleMenuConfiguration,
	surface: WritingStyleMenuSurface,
): WritingStyleMenuItem[] =>
	configuration.items
		.map((item, declarationOrder) => ({
			item,
			declarationOrder,
			rank: item.placements?.[surface] ?? declarationOrder,
			isVisible: !item.placements || item.placements[surface] !== undefined,
		}))
		.filter(({ isVisible }) => isVisible)
		.sort((a, b) => a.rank - b.rank || a.declarationOrder - b.declarationOrder)
		.map(({ item }) => item);

export const getWritingStyleMenuActionItemsForSurface = (
	configuration: WritingStyleMenuConfiguration,
	surface: WritingStyleMenuSurface,
): Array<{
	hasSeparator: boolean;
	item: Exclude<WritingStyleMenuItem, { type: 'separator' }>;
}> => {
	let nextItemHasSeparator = false;

	return getWritingStyleMenuItemsForSurface(configuration, surface).flatMap((item) => {
		if (item.type === 'separator') {
			nextItemHasSeparator = true;
			return [];
		}

		const hasSeparator = nextItemHasSeparator;
		nextItemHasSeparator = false;
		return [{ hasSeparator, item }];
	});
};

export const getWritingStyleSelectionToolbarDropdown = (
	configuration: WritingStyleMenuConfiguration,
): SelectionToolbarDropdownConfig => ({
	id: configuration.id ?? 'change-tone',
	label: getWritingStyleMenuLabelForSurface(configuration, 'selection-toolbar'),
	options: getWritingStyleMenuItemsForSurface(configuration, 'selection-toolbar').map((item) => {
		switch (item.type) {
			case 'rewrite':
				return {
					label: item.label,
					configItem: item.configItem.config,
				};
			case 'separator':
				return item;
			case 'local-action':
				return {
					type: item.type,
					id: item.id,
					label: item.label,
					onClick: item.onClick,
				};
		}
	}),
});

export const getWritingStyleRewriteConfigItems = (
	configuration: WritingStyleMenuConfiguration,
	surface: WritingStyleMenuSurface,
): EditorPluginAIConfigItemWithOptions[] =>
	getWritingStyleMenuItemsForSurface(configuration, surface).flatMap((item) => {
		if (item.type !== 'rewrite') {
			return [];
		}

		return [withWritingStyleMenuNesting(item, configuration, surface)];
	});

export const getWritingStyleMenuLabelForSurface = (
	configuration: WritingStyleMenuConfiguration,
	surface: WritingStyleMenuSurface,
): MessageDescriptor => configuration.surfaceLabels?.[surface] ?? configuration.label;

export const getWritingStyleMenuId = (configuration: WritingStyleMenuConfiguration): string =>
	configuration.id ?? 'change-tone-to';

export const getWritingStyleMenuTestId = (configuration: WritingStyleMenuConfiguration): string =>
	configuration.testId ?? getWritingStyleMenuId(configuration);

export const getWritingStyleMenuParentConfigItem = (
	configuration: WritingStyleMenuConfiguration,
	surface: WritingStyleMenuSurface,
): EditorPluginAIConfigItem | undefined =>
	configuration.parentConfigItem ??
	getWritingStyleMenuItemsForSurface(configuration, surface).find(
		(item): item is WritingStyleMenuRewriteItem => item.type === 'rewrite',
	)?.configItem.config;

const withWritingStyleMenuNesting = (
	item: WritingStyleMenuRewriteItem,
	configuration: WritingStyleMenuConfiguration,
	surface: WritingStyleMenuSurface,
): EditorPluginAIConfigItemWithOptions => ({
	...item.configItem,
	config: {
		...item.configItem.config,
		nestingConfig: {
			...item.configItem.config.nestingConfig,
			id: configuration.id ?? item.configItem.config.nestingConfig?.id ?? 'change-tone-to',
			parentTestId:
				configuration.testId ??
				configuration.id ??
				item.configItem.config.nestingConfig?.parentTestId ??
				'change-tone-to',
			parentTitle: getWritingStyleMenuLabelForSurface(configuration, surface),
			shortTitle: item.configItem.config.nestingConfig?.shortTitle ?? item.label,
		},
	},
});
